'use strict';

import * as msal from "@azure/msal-browser";
import authActions from 'actions/AuthActionCreators';
import { UserManager, WebStorageStateStore, Log } from 'oidc-client-ts';
import storageUtils from "services/storageUtils";
import appConstants from '../constants/appConstants';
import { LogLevel } from '@azure/msal-browser';
import Api from 'services/api';

oidcEnableLogging();

export function loggerCallback(logLevel, message) {
    console.log(message);
}

const tokenRenewalOffsetSeconds = appConstants.auth.TOKEN_RENEWAL_OFFSET_SECONDS || 600;
const MAX_RENEWAL_RETRIES = 1;
const UNRECOVERABLE_RENEWAL_ERRORS = ['login_required', 'interaction_required', 'consent_required', 'invalid_grant'];
const ACTIVATION_ROUTES = ['/welcome/', '/resetpassword/'];
let token = null;
let signedIn = false;
let wasLastTokenRetrievalSuccessful = false;
let inProgress = false;
let isNewSignIn = false;
let currentUser = "";
let renewalFailureCount = 0;
let renewalInProgress = false;
const msalInstance = null;

const loggerOptions = appConstants.auth.OIDC_LOG_LEVEL ? {
    loggerCallback,
    logLevel: LogLevel[appConstants.auth.OIDC_LOG_LEVEL],
    piiLoggingEnabled: false,
} : {};

const commonConfig = {
    redirect_uri: appConstants.auth.OIDC_REDIRECT_URI,  
    post_logout_redirect_uri: appConstants.auth.OIDC_POST_LOGOUT_REDIRECT_URI,
    response_type: appConstants.auth.OIDC_RESPONSE_TYPE,
    response_mode: appConstants.auth.OIDC_RESPONSE_MODE,
    userStore: new WebStorageStateStore({ store: window.sessionStorage }),
    stateStore: new WebStorageStateStore({ store: window.sessionStorage }),
};

const accessIdPConfig = {
    client_id: appConstants.auth.ACCESSIDP_CLIENT_ID,
    authority: appConstants.auth.ACCESSIDP_AUTHORITY,
    knownAuthorities: [appConstants.auth.ACCESSIDP_KNOWN_AUTHORITIES],
    scope: appConstants.auth.ACCESSIDP_SCOPES,
    loadUserInfo: true,
    ...commonConfig,
};

const b2CConfig = {    
    client_id: appConstants.auth.B2C_CLIENT_ID,
    authority:  appConstants.auth.B2C_SIGNIN_AUTHORITY,    
    scope: "openid",
    silent_redirect_uri: window.location.origin + '/silent_renew.html',
    ...commonConfig,
};

const msalConfig = {
    auth: {
        clientId: appConstants.auth.B2C_CLIENT_ID, 
        authority:  appConstants.auth.B2C_SIGNUP_AUTHORITY, 
        knownAuthorities: [appConstants.auth.B2C_KNOWN_AUTHORITIES], 
        redirectUri: appConstants.auth.B2C_REDIRECT_URI,
        postLogoutRedirectUri: appConstants.auth.OIDC_POST_LOGOUT_REDIRECT_URI,
        navigateToLoginRequestUrl: false,
    },
    cache: {
        cacheLocation: appConstants.auth.CACHE_LOCATION,
        storeAuthStateInCookie: false, 
    },
    system: {
        loggerOptions,
        tokenRenewalOffsetSeconds: appConstants.auth.TOKEN_RENEWAL_OFFSET_SECONDS,
    },
};
const b2csignUpRequest = {
    scopes: ["openid", appConstants.auth.B2C_SCOPES], 
    extraQueryParameters : {},
};

var userManagerInstance = userManagerInstance || new UserManager(getUserIdPConfig());

function oidcEnableLogging(level = appConstants.auth.OIDC_LOG_LEVEL) {
    switch (level) {
        case 'DEBUG':
            Log.setLevel(Log.DEBUG);
            break;
        case 'INFO':
            Log.setLevel(Log.INFO);
            break;
        case 'WARN':
            Log.setLevel(Log.WARN);
            break;
        case 'ERROR':
            Log.setLevel(Log.ERROR);
            break;
        default:
            Log.setLevel(Log.NONE);  // Default to NONE if no valid level is set
    }
    Log.setLogger(console);
}

function selectCachedUser() {
    currentUser = storageUtils.getUserFromStorage();
    if (!currentUser) {        
        console.log("No User");
        inProgress = true;
        return;
    }
}

function handleResponse(response, isRenew = false) {
    if (!isRenew && inProgress && response) { // is a new sign in
        isNewSignIn = true;
    }
    inProgress = false;   

    if (response !== null && response.access_token && response.access_token !== "") {
        currentUser = response;
        storageUtils.setUserToStorage(currentUser);
        token = response.access_token;
    }

    if (response === null && signedIn) { 
        return;
    }

    selectCachedUser();
    if (!isRenew) {
        authActions.pageReady(isNewSignIn);
        isNewSignIn = false;
    }
}

function handleSignInError() {
    authActions.signInError();
}

function checkForToken() {
    selectCachedUser();
    if (currentUser && !token) {
        signedIn = true;
        getTokenRedirect();
    } else {
        signedIn = false;
    }    
}

function getCurrentUser() {
    if (currentUser) {
        return currentUser;
    }
    return;    
}

function login(loginHint = null) {
    var idPConfig = getUserIdPConfig();
    userManagerInstance = new UserManager(idPConfig);
    userManagerInstance.signinRedirect(loginHint ? { login_hint: loginHint } : undefined);
}

function logOut() {
    if (currentUser?.access_token) {
        Api.b2cLogout().catch(err => {
            console.error('B2C logout error (continuing with OIDC logout):', err);
        });
        Api.accessIdentityLogout().catch(err => {
            console.error('Access Identity logout error (continuing with OIDC logout):', err);
        });
    }
    
    storageUtils.clearStorage();
    storageUtils.broadcastSignOut();
    userManagerInstance.signoutRedirect ({
        id_token_hint: currentUser?.id_token,
    });
}

function clearSession() {
    token = null;
    currentUser = "";
    signedIn = false;
    renewalFailureCount = 0;
    wasLastTokenRetrievalSuccessful = false;
}

function signUp(token = null) {
    const msalInstance = msalInstance || new msal.PublicClientApplication(msalConfig);
    b2csignUpRequest.extraQueryParameters = {
        id_token_hint: token,
        prompt: 'login',
    };
    storageUtils.setSignUpInProgress();
    msalInstance.loginRedirect(b2csignUpRequest);
}

function completeSignUp() {
    clearSession();
    storageUtils.clearStorage();
    storageUtils.setUserIdPToStorage(appConstants.ENUMS.IDENTITY_PROVIDER.B2C);
    login();
}

function isActivationInProgress() {
    if (storageUtils.isSignUpInProgress()) {
        return true;
    }
    const currentRoute = window.location.hash || '';
    return ACTIVATION_ROUTES.some(route => currentRoute.includes(route));
}

function isTokenExpiringSoon(token, bufferTimeInSeconds = 60) {
    const decodedToken = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Math.floor(Date.now() / 1000);
    const tokenExpiryTime  = decodedToken.exp;
    const remainingTime = tokenExpiryTime - currentTime;

    return remainingTime <= bufferTimeInSeconds;
}

function isUnrecoverableRenewalError(error) {
    const message = (error?.message || '').toLowerCase();
    return UNRECOVERABLE_RENEWAL_ERRORS.some(code => message.includes(code));
}

function restartSignIn() {
    const userIdP = storageUtils.getUserIdPFromStorage();
    clearSession();
    storageUtils.clearStorage();
    storageUtils.setUserIdPToStorage(userIdP);
    userManagerInstance.signinRedirect().catch(showSignInPage);
}

function showSignInPage(error) {
    console.error('Unable to reach the sign-in service:', error);
    window.location.replace('/');
}

function handleRenewalFailure(error, isRenew) {
    console.error('Failed to renew ID token:', error);
    wasLastTokenRetrievalSuccessful = false;
    renewalFailureCount += 1;

    const canRetryLater = isRenew && !isUnrecoverableRenewalError(error) && renewalFailureCount <= MAX_RENEWAL_RETRIES;
    if (!canRetryLater && !isActivationInProgress()) {
        restartSignIn();
    }
}

function getTokenRedirect(isRenew = false) {
    const currentUser = storageUtils.getUserFromStorage();
    
    if (!currentUser) {
        console.log("No user is currently logged in.");
        return;
    }

    if (currentUser) {
        const tokenExpiring = isTokenExpiringSoon(currentUser.access_token);
        if (tokenExpiring) {
            if (renewalInProgress) {
                console.log("Token renewal already in progress, skipping duplicate attempt.");
                return;
            }
            console.log("Token is expiring, attempting silent sign-in...");
            try {
                renewalInProgress = true;
                const signinSilentArgs = {
                    redirect_uri: commonConfig.redirect_uri,
                };
                userManagerInstance.signinSilent(signinSilentArgs)
                    .then(response => {
                        console.log("Token successfully refreshed:", response);
                        wasLastTokenRetrievalSuccessful = true;
                        renewalFailureCount = 0;
                        return handleResponse(response, isRenew);
                    })
                    .catch(error => handleRenewalFailure(error, isRenew))
                    .finally(() => {
                        renewalInProgress = false;
                    });
            } catch (error) {
                renewalInProgress = false;
                handleRenewalFailure(error, isRenew);
            }
        } else {
            console.log("Token is still valid.");
            wasLastTokenRetrievalSuccessful = true;
            renewalFailureCount = 0;
            return handleResponse(currentUser, isRenew);
        }
    }
}

function getToken() {
    if (!wasLastTokenRetrievalSuccessful) {
        getTokenRedirect(true);
    } else {
        try {
            return token;
        } catch (error) {
            console.log(error);
        }
    }
}

function getUserIdPConfig(userIdP = storageUtils.getUserIdPFromStorage()) {
    if (userIdP === null || userIdP === undefined || userIdP === "") {
        console.error("No User IdP found");
        return b2CConfig;
    }
    if (userIdP === appConstants.ENUMS.IDENTITY_PROVIDER.ACCESS) {
        return accessIdPConfig;
    }
    return b2CConfig;
}

setInterval(function () {
    getTokenRedirect(true);
}.bind(this), tokenRenewalOffsetSeconds * 1000);

export { msalInstance, login, logOut, signUp, completeSignUp, clearSession, getTokenRedirect, getCurrentUser, checkForToken, handleResponse, getToken, getUserIdPConfig, oidcEnableLogging };
